import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UploadSimple, FilmSlate, Presentation, FileArrowDown, Star, CaretUp, CaretDown, Handshake, UserCircle, Check, ClockCounterClockwise, DownloadSimple, Eye, BellRinging, Broadcast, BookOpen, ClosedCaptioning, PaperPlaneTilt, HardDrives } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";
import { useSettings } from "../context/SettingsContext";
import api, { fileUrl } from "../lib/api";
import TranscriptManagerModal from "../components/TranscriptManagerModal";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const EMPTY = { title: "", description: "", category: "video", vimeo_url: "", youtube_url: "", speaker: "", tags: "", recorded_at: "", file_id: null, file_name: null, file_type: null };

const catIcon = (c) => (c === "video" ? FilmSlate : c === "presentation" ? Presentation : c === "sponsor" ? Handshake : FileArrowDown);

export default function Admin() {
  const { t } = useLang();
  const { sponsorsEnabled, setSponsorsEnabled, setLiveEnabled, refresh } = useSettings();
  const [items, setItems] = useState([]);
  const [regs, setRegs] = useState([]);
  const [stats, setStats] = useState(null);
  const [storage, setStorage] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestDialog, setDigestDialog] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [viewersFor, setViewersFor] = useState(null); // { id, title, viewers }
  const [ytStatus, setYtStatus] = useState({ configured: false, connected: false });
  const [ytDialog, setYtDialog] = useState(false);
  const [ytForm, setYtForm] = useState({ title: "", description: "", scheduled_at: "", privacy: "unlisted" });
  const [ytCreating, setYtCreating] = useState(false);
  const [liveStartedAt, setLiveStartedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [presenceCount, setPresenceCount] = useState(0);
  const [presencePeak, setPresencePeak] = useState(0);
  const [broadcasts, setBroadcasts] = useState([]);
  const [liveForm, setLiveForm] = useState({ live_enabled: false, live_url: "", live_title: "", live_scheduled_at: "", chat_enabled: false, gopro_live_url: "" });
  // RTMP credentials shown after "Create YouTube Live" so the admin can
  // configure a GoPro / OBS / Wirecast encoder against this broadcast.
  const [rtmpInfo, setRtmpInfo] = useState(null); // { rtmp_url, rtmp_backup_url, stream_key, watch_url, title }
  const [rtmpDialog, setRtmpDialog] = useState(false);
  const [streamKeyVisible, setStreamKeyVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [transcriptFor, setTranscriptFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/content").then((res) => setItems(res.data)).catch(() => {});
    api.get("/registrations").then((res) => setRegs(res.data)).catch(() => {});
    api.get("/stats").then((res) => setStats(res.data)).catch(() => {});
    api.get("/admin/storage").then((res) => setStorage(res.data)).catch(() => {});
    api.get("/stats/timeline?days=30").then((res) => setTimeline(res.data.timeline || [])).catch(() => {});
    api.get("/youtube/status").then((res) => setYtStatus(res.data)).catch(() => {});
    api.get("/live/broadcasts").then((res) => setBroadcasts(res.data || [])).catch(() => {});
    api.get("/live").then((res) => { setLiveForm({ live_enabled: !!res.data.enabled, live_url: res.data.url || "", live_title: res.data.title || "", live_scheduled_at: res.data.scheduled_at || "", chat_enabled: !!res.data.chat_enabled, gopro_live_url: res.data.gopro_live_url || "" }); setLiveStartedAt(res.data.started_at || null); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveLive = async (enabledOverride, chatOverride) => {
    const payload = {
      ...liveForm,
      live_enabled: enabledOverride !== undefined ? enabledOverride : liveForm.live_enabled,
      chat_enabled: chatOverride !== undefined ? chatOverride : liveForm.chat_enabled,
    };
    try {
      const { data: putResp } = await api.put("/live", payload);
      setLiveForm(payload);
      setLiveEnabled(payload.live_enabled);
      // Refetch live state so the "on-air since" timestamp is authoritative.
      try {
        const { data } = await api.get("/live");
        setLiveStartedAt(data.started_at || null);
      } catch {}
      refresh();
      if (putResp?.notified) {
        toast.success("🔴 Live ON — Email di notifica inviate a tutti i membri approvati");
      } else {
        toast.success(payload.live_enabled ? "Live is ON" : "Live saved");
      }
    } catch { toast.error("Could not save live settings"); }
  };

  const saveLiveToLibrary = async () => {
    try {
      const { data } = await api.post("/live/save-to-library", { title: liveForm.live_title || undefined });
      toast.success("Live saved to library");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const notifyMembers = async () => {
    if (!window.confirm("Send a 'we are live' email to all approved members?")) return;
    try {
      const { data } = await api.post("/live/notify");
      toast.success(`Notified ${data.notified} members`);
    } catch { toast.error("Notify failed"); }
  };

  const sendLiveReminder = async () => {
    if (!liveForm.live_scheduled_at) { toast.error("Set a scheduled date first"); return; }
    if (!window.confirm("Send the 1-hour reminder email to all approved members now?")) return;
    try {
      const { data } = await api.post("/live/remind-now");
      toast.success(`Reminder sent to ${data.notified} members`);
    } catch (err) { toast.error(err.response?.data?.detail || "Reminder failed"); }
  };

  const connectYouTube = async () => {
    try {
      const { data } = await api.get("/youtube/auth-url");
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "OAuth not available"); }
  };

  const disconnectYouTube = async () => {
    if (!window.confirm("Scollegare l'account YouTube?")) return;
    try {
      await api.post("/youtube/disconnect");
      setYtStatus({ ...ytStatus, connected: false });
      toast.success("YouTube disconnesso");
    } catch { toast.error("Failed"); }
  };

  const createYouTubeBroadcast = async () => {
    if (!ytForm.title.trim()) { toast.error("Serve un titolo"); return; }
    setYtCreating(true);
    try {
      const { data } = await api.post("/live/youtube/create", {
        title: ytForm.title,
        description: ytForm.description,
        scheduled_at: ytForm.scheduled_at || null,
        privacy: ytForm.privacy,
      });
      // Server already populated Wall Board Academy live settings; sync the local form:
      setLiveForm({
        live_enabled: liveForm.live_enabled,
        chat_enabled: liveForm.chat_enabled,
        live_url: data.watch_url,
        live_title: ytForm.title,
        live_scheduled_at: data.scheduled_at,
      });
      setYtDialog(false);
      setYtForm({ title: "", description: "", scheduled_at: "", privacy: "unlisted" });
      // Immediately show RTMP credentials so the admin can configure GoPro/OBS
      if (data.rtmp_url && data.stream_key) {
        setRtmpInfo({
          watch_url: data.watch_url,
          title: ytForm.title,
          rtmp_url: data.rtmp_url,
          rtmp_backup_url: data.rtmp_backup_url,
          stream_key: data.stream_key,
        });
        setStreamKeyVisible(false);
        setRtmpDialog(true);
      } else {
        toast.success("Diretta YouTube creata e URL sincronizzato");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione diretta");
    } finally {
      setYtCreating(false);
    }
  };

  const showRtmpCredentials = async () => {
    try {
      const { data } = await api.get("/live/youtube/stream-info");
      setRtmpInfo(data);
      setStreamKeyVisible(false);
      setRtmpDialog(true);
    } catch (err) {
      const code = err.response?.status;
      if (code === 404) {
        toast.error("Nessuna diretta con credenziali RTMP salvate. Crea prima una diretta YouTube.");
      } else {
        toast.error("Impossibile recuperare le credenziali RTMP");
      }
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiato negli appunti`);
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const endLiveOneClick = async () => {
    if (!window.confirm("Terminare la diretta ora?\n\n• Chiude lo stream su YouTube\n• Spegne il live sul sito\n• Spegne la chat\n• Salva la registrazione in libreria\n• Svuota i campi\n\nProcedere?")) return;
    try {
      // 1) Try to end broadcast on YouTube (best effort)
      if (ytStatus.connected && liveForm.live_url && liveForm.live_url.includes("watch?v=")) {
        try { await api.post("/live/youtube/end"); } catch { /* not fatal */ }
      }
      // 2) Save recording to library (only if a URL exists)
      if (liveForm.live_url) {
        try { await api.post("/live/save-to-library", { title: liveForm.live_title || undefined }); } catch (e) { console.error("save-to-library failed", e); }
      }
      // 3) Turn off live + chat + clear fields
      const cleared = { live_enabled: false, chat_enabled: false, live_url: "", live_title: "", live_scheduled_at: "", gopro_live_url: "" };
      await api.put("/live", cleared);
      setLiveForm(cleared);
      setLiveEnabled(false);
      setLiveStartedAt(null);
      // Refresh broadcast history to show the new row
      try { const { data } = await api.get("/live/broadcasts"); setBroadcasts(data || []); } catch {}
      refresh();
      toast.success("Diretta terminata · registrazione salvata in libreria");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore chiusura diretta");
    }
  };

  // Show OAuth callback result once
  useEffect(() => {
    const url = new URL(window.location.href);
    const yt = url.searchParams.get("yt");
    if (yt === "connected") { toast.success("YouTube connesso"); url.searchParams.delete("yt"); window.history.replaceState({}, "", url.pathname); api.get("/youtube/status").then((r) => setYtStatus(r.data)); }
    else if (yt === "error") { toast.error("Errore autorizzazione YouTube"); url.searchParams.delete("yt"); window.history.replaceState({}, "", url.pathname); }
  }, []);

  const toggleChat = () => saveLive(undefined, !liveForm.chat_enabled);

  const sendReminder = async () => {
    try {
      const { data } = await api.post("/registrations/remind");
      toast.success(`${t("reminder_sent")} (${data.pending_reminded})`);
    } catch { toast.error("Failed"); }
  };

  const sendReport = async () => {
    try {
      await api.post("/reports/monthly");
      toast.success(t("report_sent"));
    } catch { toast.error("Failed"); }
  };

  const openDigest = async () => {
    setDigestDialog(true);
    setDigestPreview(null);
    try {
      const { data } = await api.get("/admin/notify-new-content/preview");
      setDigestPreview(data);
    } catch { toast.error("Failed"); setDigestDialog(false); }
  };

  const confirmDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/notify-new-content");
      if (!data.new_count) {
        toast.info(t("digest_none"));
      } else {
        toast.success(t("digest_sent").replace("{n}", data.sent).replace("{c}", data.new_count));
      }
      setDigestDialog(false);
    } catch { toast.error("Failed"); }
    finally { setSendingDigest(false); }
  };

  const fmtBytes = (b) => {
    if (!b) return "0 MB";
    const gb = b / (1024 ** 3);
    return gb >= 0.1 ? `${gb.toFixed(2)} GB` : `${Math.max(1, Math.round(b / (1024 ** 2)))} MB`;
  };

  const openViewers = async (c) => {
    try {
      const { data } = await api.get(`/stats/viewers/${c.id}`);
      setViewersFor({ id: c.id, title: c.title, viewers: data.viewers || [] });
    } catch { toast.error("Failed to load viewers"); }
  };

  const fmtWatch = (s) => {
    if (!s) return "0s";
    const m = Math.floor(s / 60);
    const rest = s % 60;
    return m > 0 ? `${m}m ${rest}s` : `${rest}s`;
  };

  const fmtElapsed = (ms) => {
    if (!ms || ms < 0) return "0m";
    const total = Math.floor(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  };

  // Live "on-air" ticker: refreshes every second while a live is active.
  useEffect(() => {
    if (!liveForm.live_enabled || !liveStartedAt) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [liveForm.live_enabled, liveStartedAt]);

  // Poll the private admin-only viewer counter every 10s while a live is on.
  useEffect(() => {
    if (!liveForm.live_enabled) { setPresenceCount(0); setPresencePeak(0); return; }
    const fetchCount = () => api.get("/live/presence-count").then((r) => { setPresenceCount(r.data.count || 0); setPresencePeak(r.data.peak || 0); }).catch(() => {});
    fetchCount();
    const t = setInterval(fetchCount, 10000);
    return () => clearInterval(t);
  }, [liveForm.live_enabled]);

  const toggleSponsors = async () => {
    try {
      const next = !sponsorsEnabled;
      await api.put("/settings", { sponsors_enabled: next });
      setSponsorsEnabled(next);
      refresh();
      toast.success(next ? "Sponsor page enabled" : "Sponsor page hidden");
    } catch { toast.error("Could not update settings"); }
  };

  const approveReg = async (id) => {
    try {
      const { data } = await api.post(`/registrations/${id}/approve-admin`);
      toast.success(data.message || "Approved");
      load();
    } catch (e) {
      toast.error("Approval failed");
    }
  };

  const rejectReg = async (id) => {
    try {
      await api.post(`/registrations/${id}/reject-admin`);
      toast.success("Request rejected");
      load();
    } catch { toast.error("Action failed"); }
  };

  const deleteReg = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    try {
      await api.delete(`/registrations/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/registrations/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "wba-registrations.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: fileUrl(data.file_id) }));
      toast.success("Logo uploaded");
    } catch { toast.error("Logo upload failed"); }
    finally { setUploading(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...EMPTY, ...it, tags: Array.isArray(it.tags) ? it.tags.join(", ") : (it.tags || ""), recorded_at: it.recorded_at ? String(it.recorded_at).slice(0, 16) : "" }); setEditing(it.id); setOpen(true); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ext = (data.ext || "").toLowerCase();
      const ftype = ext === "mp4" ? "mp4" : ext.includes("ppt") ? "pptx" : ext;
      setForm((f) => ({
        ...f,
        file_id: data.file_id,
        file_name: data.file_name,
        file_type: ftype,
        // Auto-fill thumbnail from generated cover (PDF/PPTX) if the admin hasn't
        // provided a custom one yet. Admin can still override it manually.
        thumbnail: (!f.thumbnail && data.thumbnail_url) ? data.thumbnail_url : f.thumbnail,
      }));
      toast.success(data.thumbnail_url ? "File uploaded — cover auto-generated" : "File uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, category: form.category,
        vimeo_url: form.vimeo_url || null, youtube_url: form.youtube_url || null, speaker: form.speaker || null,
        file_id: form.file_id || null, file_name: form.file_name || null, file_type: form.file_type || null,
        thumbnail: form.thumbnail || null,
        link_url: form.link_url || null, logo_url: form.logo_url || null,
        recorded_at: form.recorded_at || null,
        tags: Array.isArray(form.tags) ? form.tags : String(form.tags || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) await api.put(`/content/${editing}`, payload);
      else await api.post("/content", payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/content/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggleFeatured = async (item) => {
    try {
      await api.patch(`/content/${item.id}`, { featured: !item.featured });
      load();
    } catch { toast.error("Update failed"); }
  };

  const promoteToSlot = async (item, slot) => {
    try {
      await api.post(`/admin/content/${item.id}/promote`, { slot });
      toast.success(slot ? `Promoted to Home slot ${slot}` : "Unpinned from Home");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Promote failed"); }
  };

  const move = async (item, dir) => {
    const siblings = items.filter((i) => i.category === item.category);
    const idx = siblings.findIndex((i) => i.id === item.id);
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];
    try {
      await Promise.all(reordered.map((it, i) => api.patch(`/content/${it.id}`, { order: i })));
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const inputCls = "w-full border border-white/15 bg-ink-900/60 px-4 py-3 font-body text-sm text-white outline-none transition-colors focus:border-cyan";

  return (
    <div data-testid="admin-page" className="min-h-screen px-6 pt-28 pb-28 md:px-10">
      <div className="mx-auto max-w-[1400px]">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-4 border-b border-white/10 pb-8 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">— {t("admin")}</p>
            <h1 className="mt-3 font-head text-4xl font-black uppercase tracking-tight text-white md:text-5xl">{t("admin_dashboard")}</h1>
            <p className="mt-3 font-body text-sm text-slate-400">{t("manage_content")}</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="font-head text-3xl font-black text-cyan">{items.length}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t("total_items")}</p>
            </div>
            <button
              data-testid="add-content-btn"
              onClick={openNew}
              className="flex items-center gap-2 bg-cyan px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink-900 transition-colors hover:bg-white"
            >
              <Plus size={16} weight="bold" /> {t("add_content")}
            </button>
          </div>
        </motion.div>

        {/* Statistics */}
        {stats && (
          <div data-testid="stats-panel" className="mt-8">
            <div className="mb-4 flex items-center gap-3">
              <span className="font-head text-lg font-bold uppercase tracking-tight text-white">{t("statistics")}</span>
              <span className="h-px flex-1 bg-white/10" />
              <Link data-testid="admin-help-btn" to="/admin/help" className="flex items-center gap-2 border border-cyan/50 bg-cyan/5 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-cyan transition-colors hover:bg-cyan hover:text-ink-900">
                <BookOpen size={14} weight="bold" /> Guida
              </Link>
              <button data-testid="send-report-btn" onClick={sendReport} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-300 transition-colors hover:border-cyan hover:text-cyan">
                <BellRinging size={14} /> {t("send_report")}
              </button>
              <button data-testid="send-digest-btn" onClick={openDigest} disabled={sendingDigest} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-300 transition-colors hover:border-cyan hover:text-cyan disabled:opacity-50">
                <PaperPlaneTilt size={14} /> {sendingDigest ? "…" : t("send_digest")}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                { label: t("members_label"), value: stats.members.total, sub: `${stats.members.pending} ${t("pending").toLowerCase()}`, Icon: UserCircle },
                { label: t("approved"), value: stats.members.approved, sub: `${stats.members.rejected} ${t("rejected").toLowerCase()}`, Icon: Check },
                { label: t("total_items"), value: stats.content.total, sub: Object.entries(stats.content.by_category).map(([k, v]) => `${v} ${k}`).join(" · "), Icon: FilmSlate },
                { label: t("views_label"), value: stats.total_views, sub: t("most_viewed").toLowerCase(), Icon: Eye },
              ].map((s, i) => (
                <div key={i} className="border border-white/10 bg-ink-700/30 p-5">
                  <div className="flex items-center justify-between">
                    <p className="font-head text-3xl font-black text-cyan">{s.value}</p>
                    <s.Icon size={22} className="text-slate-500" />
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">{s.label}</p>
                  <p className="mt-0.5 truncate font-mono text-[9px] uppercase tracking-[0.15em] text-slate-600">{s.sub}</p>
                </div>
              ))}
            </div>
            {storage && (
              <div data-testid="storage-panel" className="mt-4 border border-white/10 bg-ink-700/30 p-5">
                <div className="flex items-center justify-between gap-4">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
                    <HardDrives size={15} className="text-slate-500" /> {t("storage_title")}
                  </p>
                  <p data-testid="storage-usage-text" className="font-mono text-[11px] text-cyan">
                    {fmtBytes(storage.used_bytes)} / {fmtBytes(storage.quota_bytes)}
                  </p>
                </div>
                {(() => {
                  const pct = Math.min(100, Math.round((storage.used_bytes / storage.quota_bytes) * 100));
                  return (
                    <div className="mt-3 h-2 w-full bg-ink-900/80">
                      <div
                        data-testid="storage-usage-bar"
                        className={`h-2 transition-[width] duration-700 ${pct > 85 ? "bg-red-500" : pct > 65 ? "bg-amber-400" : "bg-cyan"}`}
                        style={{ width: `${Math.max(1, pct)}%` }}
                      />
                    </div>
                  );
                })()}
                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-600">
                  {storage.file_count} file · {t("storage_hint")}
                </p>
              </div>
            )}
            {stats.top_viewed.length > 0 && (
              <div className="mt-6 border border-white/10 bg-ink-700/20 p-6">
                <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">{t("most_viewed")}</p>
                <div className="flex flex-col gap-2">
                  {stats.top_viewed.map((c, i) => (
                    <button
                      key={c.id}
                      data-testid={`top-viewed-${c.id}`}
                      onClick={() => openViewers(c)}
                      className="flex items-center justify-between border-b border-white/5 pb-2 text-left transition-colors last:border-0 hover:text-cyan"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="font-head text-sm text-slate-600">{String(i + 1).padStart(2, "0")}</span>
                        <span className="truncate font-body text-sm text-slate-200">{c.title}</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest text-slate-600">{c.category}</span>
                      </div>
                      <span className="flex shrink-0 items-center gap-3">
                        {c.avg_watch_seconds > 0 && (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">avg {fmtWatch(c.avg_watch_seconds)}</span>
                        )}
                        <span className="flex items-center gap-1.5 font-mono text-xs text-cyan"><Eye size={13} /> {c.views}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <p className="mt-3 font-mono text-[9px] uppercase tracking-widest text-slate-600">Clicca su un contenuto per vedere chi l'ha guardato</p>
              </div>
            )}
            {timeline.length > 0 && (
              <div data-testid="timeline-chart" className="mt-6 border border-white/10 bg-ink-700/20 p-6">
                <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Visualizzazioni · ultimi 30 giorni</p>
                <TimelineChart data={timeline} />
                <div className="mt-3 flex items-center justify-between font-mono text-[9px] uppercase tracking-widest text-slate-500">
                  <span>{timeline[0]?.date}</span>
                  <span>totale: {timeline.reduce((a, b) => a + (b.views || 0), 0)}</span>
                  <span>{timeline[timeline.length - 1]?.date}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 flex flex-col gap-10">
          {items.length === 0 && <p className="mt-10 font-body text-slate-500">{t("empty_section")}</p>}
          {["video", "presentation", "other", "sponsor"].map((cat) => {
            const group = items.filter((i) => i.category === cat);
            if (group.length === 0) return null;
            const label = cat === "video" ? t("video") : cat === "presentation" ? t("presentations") : cat === "sponsor" ? t("supported_by") : t("other");
            return (
              <div key={cat} data-testid={`admin-group-${cat}`}>
                <div className="mb-3 flex items-center gap-3">
                  <span className="font-head text-lg font-bold uppercase tracking-tight text-white">{label}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{group.length}</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <div className="flex flex-col gap-3">
                  {group.map((it, gi) => {
                    const Icon = catIcon(it.category);
                    return (
                      <div key={it.id} data-testid={`admin-row-${it.id}`} className="group flex items-center justify-between border border-white/10 bg-ink-700/30 px-4 py-4 transition-colors hover:border-cyan/30 md:px-6">
                        <div className="flex min-w-0 items-center gap-3 md:gap-4">
                          <div className="flex flex-col">
                            <button data-testid={`move-up-${it.id}`} onClick={() => move(it, "up")} disabled={gi === 0} className="text-slate-500 transition-colors hover:text-cyan disabled:opacity-20">
                              <CaretUp size={14} weight="bold" />
                            </button>
                            <button data-testid={`move-down-${it.id}`} onClick={() => move(it, "down")} disabled={gi === group.length - 1} className="text-slate-500 transition-colors hover:text-cyan disabled:opacity-20">
                              <CaretDown size={14} weight="bold" />
                            </button>
                          </div>
                          <Icon size={22} className="shrink-0 text-cyan" />
                          <div className="min-w-0">
                            <p className="truncate font-head text-base text-white">{it.title}</p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                              {it.category}{it.speaker ? ` · ${it.speaker}` : ""}{it.file_name ? ` · ${it.file_name}` : ""}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Home slot promote (only for content categories shown in Home) */}
                          {["video", "presentation", "other"].includes(it.category) && (
                            <div className="flex items-center gap-1 border border-white/15 px-1 py-0.5" title="Promote to Home slot">
                              {[1, 2, 3].map((n) => {
                                const on = it.home_slot === n;
                                return (
                                  <button
                                    key={n}
                                    data-testid={`promote-${it.id}-${n}`}
                                    onClick={() => promoteToSlot(it, on ? null : n)}
                                    className="h-6 w-6 font-mono text-[10px] font-bold transition-colors"
                                    style={{ backgroundColor: on ? "#00F0FF" : "transparent", color: on ? "#050810" : "#94A3B8" }}
                                    title={on ? `Unpin from slot ${n}` : `Promote to Home slot ${n}`}
                                  >
                                    {n}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <button data-testid={`feature-${it.id}`} onClick={() => toggleFeatured(it)} title="Feature" className="border p-2.5 transition-colors" style={{ borderColor: it.featured ? "#00F0FF" : "rgba(255,255,255,0.15)", color: it.featured ? "#00F0FF" : "#94A3B8" }}>
                            <Star size={16} weight={it.featured ? "fill" : "regular"} />
                          </button>
                          <button data-testid={`edit-${it.id}`} onClick={() => openEdit(it)} className="border border-white/15 p-2.5 text-slate-300 transition-colors hover:border-cyan hover:text-cyan">
                            <PencilSimple size={16} />
                          </button>
                          {it.category === "video" && (
                            <button data-testid={`transcript-${it.id}`} title="Manage transcript" onClick={() => setTranscriptFor(it)} className="border border-white/15 p-2.5 text-slate-300 transition-colors hover:border-cyan hover:text-cyan">
                              <ClosedCaptioning size={16} />
                            </button>
                          )}
                          <button data-testid={`delete-${it.id}`} onClick={() => remove(it.id)} className="border border-white/15 p-2.5 text-slate-300 transition-colors hover:border-red-500 hover:text-red-400">
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* YouTube OAuth connection */}
        <div data-testid="youtube-panel" className="mt-12 border border-white/10 bg-ink-700/30 p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-head text-lg font-bold uppercase tracking-tight text-white">YouTube integration</p>
              <p className="mt-1 truncate font-body text-sm text-slate-400">
                {!ytStatus.configured
                  ? "Non configurato lato server. Aggiungi GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET in backend/.env"
                  : ytStatus.connected
                  ? `Connesso · canale: ${ytStatus.channel_title || "—"}`
                  : "Non collegato. Autorizza il tuo canale YouTube per creare le dirette con un click"}
              </p>
            </div>
            <div className="shrink-0">
              {ytStatus.connected ? (
                <button data-testid="yt-disconnect-btn" onClick={disconnectYouTube} className="border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-300 transition-colors hover:border-red-500 hover:text-red-400">
                  Scollega
                </button>
              ) : (
                <button data-testid="yt-connect-btn" onClick={connectYouTube} disabled={!ytStatus.configured} className="flex items-center gap-2 bg-red-600 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-red-500 disabled:opacity-40">
                  <Broadcast size={14} weight="bold" /> Autorizza YouTube
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Live streaming */}
        <div data-testid="live-panel" className="mt-12 border border-white/10 bg-ink-700/30 p-6">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Broadcast size={22} className={liveForm.live_enabled ? "text-red-400" : "text-cyan"} />
              <span className="font-head text-lg font-bold uppercase tracking-tight text-white">Live streaming (YouTube)</span>
              {liveForm.live_enabled && liveStartedAt && (
                <span data-testid="live-onair-badge" className="ml-2 flex items-center gap-2 border border-red-500/60 bg-red-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-red-400">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  In onda da {fmtElapsed(nowTick - new Date(liveStartedAt).getTime())}
                </span>
              )}
              {liveForm.live_enabled && (
                <span data-testid="live-presence-count" title="Spettatori attivi negli ultimi 45 secondi · visibile solo all'admin" className="flex items-center gap-2 border border-cyan/50 bg-cyan/5 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan">
                  <Eye size={12} weight="bold" /> {presenceCount} {presenceCount === 1 ? "spettatore" : "spettatori"}
                  {presencePeak > 0 && <span className="text-cyan/60"> · picco {presencePeak}</span>}
                </span>
              )}
            </div>
            <button
              data-testid="live-toggle"
              onClick={() => saveLive(!liveForm.live_enabled)}
              role="switch"
              aria-checked={liveForm.live_enabled}
              className="relative h-7 w-14 rounded-full border transition-colors"
              style={{ borderColor: liveForm.live_enabled ? "#ef4444" : "rgba(255,255,255,0.2)", backgroundColor: liveForm.live_enabled ? "rgba(239,68,68,0.25)" : "transparent" }}
            >
              <span className="absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full transition-all duration-300" style={{ left: liveForm.live_enabled ? "30px" : "3px", backgroundColor: liveForm.live_enabled ? "#ef4444" : "#94A3B8" }} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">YouTube live URL</label>
              <input data-testid="live-url" className={inputCls} placeholder="https://www.youtube.com/watch?v=..." value={liveForm.live_url} onChange={(e) => setLiveForm({ ...liveForm, live_url: e.target.value })} />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Link GoPro.com per Live <span className="text-cyan/60">(alternativa a YouTube)</span>
              </label>
              <input
                data-testid="gopro-live-url"
                className={inputCls}
                placeholder="https://gopro.com/live/..."
                value={liveForm.gopro_live_url || ""}
                onChange={(e) => setLiveForm({ ...liveForm, gopro_live_url: e.target.value })}
              />
              <p className="mt-1 font-body text-[11px] text-slate-500">
                Se compili questo campo, sul sito apparirà un bottone <b className="text-cyan">"Guarda su GoPro"</b> che apre il link in una nuova scheda. Usalo quando non puoi trasmettere via YouTube (es. canale nuovo &lt;50 iscritti).
              </p>
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("title")}</label>
              <input data-testid="live-title" className={inputCls} placeholder="Live: TAR Surgery" value={liveForm.live_title} onChange={(e) => setLiveForm({ ...liveForm, live_title: e.target.value })} />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Data e ora programmata</label>
              <input data-testid="live-scheduled" type="datetime-local" className={`${inputCls} [color-scheme:dark]`} value={liveForm.live_scheduled_at || ""} onChange={(e) => setLiveForm({ ...liveForm, live_scheduled_at: e.target.value })} />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Q&amp;A domande in diretta</label>
              <button
                data-testid="chat-toggle"
                type="button"
                onClick={toggleChat}
                className="flex w-full items-center justify-between border border-white/15 bg-ink-900/60 px-4 py-3 transition-colors hover:border-cyan"
              >
                <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest" style={{ color: liveForm.chat_enabled ? "#22c55e" : "#f87171" }}>
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: liveForm.chat_enabled ? "#22c55e" : "#ef4444" }} />
                  {liveForm.chat_enabled ? "Attiva" : "Non attiva"}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{liveForm.chat_enabled ? "Disattiva" : "Attiva"}</span>
              </button>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button data-testid="yt-create-btn" onClick={() => setYtDialog(true)} className="flex items-center gap-2 bg-red-600 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-red-500 disabled:opacity-50" disabled={!ytStatus.connected}>
              <Broadcast size={14} weight="bold" /> Crea diretta YouTube
            </button>
            <button data-testid="yt-show-rtmp" onClick={showRtmpCredentials} className="flex items-center gap-2 border border-cyan/60 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-cyan transition-colors hover:bg-cyan hover:text-ink-900" disabled={!ytStatus.connected}>
              📡 Credenziali RTMP (GoPro / OBS)
            </button>
            <button data-testid="live-save" onClick={() => saveLive()} className="bg-cyan px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-white">{t("save")}</button>
            <button data-testid="live-notify" onClick={notifyMembers} className="flex items-center gap-2 border border-red-500/50 px-6 py-3 font-mono text-xs uppercase tracking-widest text-red-300 transition-colors hover:bg-red-500 hover:text-white"><BellRinging size={14} /> Avvisa i membri</button>
            <button data-testid="live-remind" onClick={sendLiveReminder} className="flex items-center gap-2 border border-cyan/50 px-6 py-3 font-mono text-xs uppercase tracking-widest text-cyan transition-colors hover:bg-cyan hover:text-ink-900"><BellRinging size={14} /> Promemoria 1h</button>
            <button data-testid="live-save-library" onClick={saveLiveToLibrary} className="border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest text-slate-200 transition-colors hover:border-cyan hover:text-cyan">Salva registrazione in libreria</button>
            <button data-testid="live-end-oneclick" onClick={endLiveOneClick} className="flex items-center gap-2 border border-red-500 bg-red-500/10 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-red-400 transition-colors hover:bg-red-500 hover:text-white">
              <Broadcast size={14} weight="bold" /> Termina diretta
            </button>
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">Quando attivo, compare il tasto ● LIVE nel menu. Il promemoria automatico parte ~1h prima dell'orario programmato. <span className="text-cyan">All'attivazione, tutti i membri approvati ricevono un'email con il link alla diretta.</span> "Termina diretta" chiude tutto con un click.</p>
        </div>

        {/* Broadcast history */}
        <div data-testid="broadcast-history" className="mt-12 border border-white/10 bg-ink-700/20 p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ClockCounterClockwise size={20} className="text-cyan" />
              <p className="font-head text-lg font-bold uppercase tracking-tight text-white">Log dirette</p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{broadcasts.length} totali</span>
            </div>
          </div>
          {broadcasts.length === 0 ? (
            <p className="py-6 text-center font-body text-sm text-slate-500">Nessuna diretta ancora archiviata. Le dirette terminate compaiono qui automaticamente.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left font-body text-sm">
                <thead>
                  <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <th className="py-2">Titolo</th>
                    <th className="py-2 text-right">Data</th>
                    <th className="py-2 text-right">Durata</th>
                    <th className="py-2 text-right">Picco</th>
                    <th className="py-2 text-right">Replay</th>
                    <th className="py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {broadcasts.map((b) => (
                    <tr key={b.id} data-testid={`broadcast-${b.id}`} className="border-b border-white/5">
                      <td className="py-2 pr-3">
                        <p className="truncate text-slate-200">{b.title}</p>
                        <p className="font-mono text-[9px] text-slate-600">{b.youtube_broadcast_id || "—"}</p>
                      </td>
                      <td className="py-2 pl-3 text-right font-mono text-[11px] text-slate-400">
                        {b.started_at ? new Date(b.started_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                      <td className="py-2 text-right font-mono text-[11px] text-slate-300">{fmtElapsed((b.duration_seconds || 0) * 1000)}</td>
                      <td className="py-2 text-right font-mono text-xs text-cyan">{b.peak_viewers ?? 0}</td>
                      <td className="py-2 text-right">
                        {b.url ? (
                          <a href={b.url} target="_blank" rel="noreferrer" className="font-mono text-[10px] uppercase tracking-widest text-cyan hover:text-white">Apri</a>
                        ) : (
                          <span className="font-mono text-[10px] text-slate-600">—</span>
                        )}
                      </td>
                      <td className="py-2 pl-3 text-right">
                        <button
                          data-testid={`broadcast-delete-${b.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Rimuovere "${b.title}" dallo storico?`)) return;
                            try {
                              await api.delete(`/live/broadcasts/${b.id}`);
                              setBroadcasts(broadcasts.filter((x) => x.id !== b.id));
                            } catch { toast.error("Errore"); }
                          }}
                          className="text-slate-500 transition-colors hover:text-red-400"
                        >
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sponsor page toggle */}
        <div data-testid="sponsor-toggle-card" className="mt-12 flex items-center justify-between border border-white/10 bg-ink-700/30 px-6 py-5">
          <div className="flex items-center gap-4">
            <Handshake size={24} className="text-cyan" />
            <div>
              <p className="font-head text-base text-white">{t("sponsor_page")}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{t("sponsor_toggle_hint")}</p>
            </div>
          </div>
          <button
            data-testid="sponsor-toggle"
            onClick={toggleSponsors}
            role="switch"
            aria-checked={sponsorsEnabled}
            className="relative h-7 w-14 rounded-full border transition-colors"
            style={{ borderColor: sponsorsEnabled ? "#00F0FF" : "rgba(255,255,255,0.2)", backgroundColor: sponsorsEnabled ? "rgba(0,240,255,0.25)" : "transparent" }}
          >
            <span className="absolute top-1/2 h-5 w-5 -translate-y-1/2 rounded-full transition-all duration-300" style={{ left: sponsorsEnabled ? "30px" : "3px", backgroundColor: sponsorsEnabled ? "#00F0FF" : "#94A3B8" }} />
          </button>
        </div>

        {/* Registrations */}
        <div data-testid="registrations-panel" className="mt-12">
          <div className="mb-4 flex items-center gap-3">
            <UserCircle size={22} className="text-cyan" />
            <span className="font-head text-lg font-bold uppercase tracking-tight text-white">{t("registrations")}</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{regs.length}</span>
            <span className="h-px flex-1 bg-white/10" />
            <button data-testid="send-reminder-btn" onClick={sendReminder} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-300 transition-colors hover:border-cyan hover:text-cyan">
              <BellRinging size={14} /> {t("send_reminder")}
            </button>
            {regs.length > 0 && (
              <button data-testid="export-csv-btn" onClick={exportCsv} className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-300 transition-colors hover:border-cyan hover:text-cyan">
                <DownloadSimple size={14} /> {t("export_csv")}
              </button>
            )}
          </div>
          {regs.length === 0 ? (
            <p className="font-body text-sm text-slate-500">{t("empty_section")}</p>
          ) : (
            <div className="flex flex-col gap-3">
              {regs.map((r) => {
                const statusColor = r.status === "approved" ? "#22c55e" : r.status === "rejected" ? "#f87171" : "#fbbf24";
                const statusLabel = r.status === "approved" ? t("approved") : r.status === "rejected" ? t("rejected") : t("pending");
                return (
                  <div key={r.id} data-testid={`reg-row-${r.id}`} className="flex flex-col gap-3 border border-white/10 bg-ink-700/30 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
                    <div className="min-w-0">
                      <p className="font-head text-base text-white">{r.last_name} {r.first_name}</p>
                      <p className="truncate font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        {r.email}{r.birth_date ? ` · ${r.birth_date}` : ""}{r.institution ? ` · ${r.institution}` : ""}{r.specialization ? ` · ${r.specialization}` : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: statusColor }}>
                        <ClockCounterClockwise size={13} /> {statusLabel}
                      </span>
                      {r.status !== "approved" && (
                        <button
                          data-testid={`approve-${r.id}`}
                          onClick={() => approveReg(r.id)}
                          className="flex items-center gap-2 bg-cyan px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-white"
                        >
                          <Check size={14} weight="bold" /> {t("approve")}
                        </button>
                      )}
                      {r.status === "pending" && (
                        <button
                          data-testid={`reject-${r.id}`}
                          onClick={() => rejectReg(r.id)}
                          className="border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-300 transition-colors hover:border-red-500 hover:text-red-400"
                        >
                          {t("rejected")}
                        </button>
                      )}
                      <button
                        data-testid={`delete-reg-${r.id}`}
                        onClick={() => deleteReg(r.id)}
                        title="Delete"
                        className="border border-white/15 p-2 text-slate-400 transition-colors hover:border-red-500 hover:text-red-400"
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="content-dialog" className="max-w-lg border-white/10 bg-ink-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl font-bold uppercase tracking-tight">
              {editing ? t("edit") : t("add_content")}
            </DialogTitle>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              {t("manage_content")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto pr-1">
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("title")}</label>
              <input data-testid="form-title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("category")}</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="form-category" className="border-white/15 bg-ink-900/60 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-ink-800 text-white">
                  <SelectItem value="video">{t("video")}</SelectItem>
                  <SelectItem value="presentation">{t("presentations")}</SelectItem>
                  <SelectItem value="other">{t("other")}</SelectItem>
                  <SelectItem value="sponsor">{t("supported_by")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("speaker")}</label>
              <input data-testid="form-speaker" className={inputCls} value={form.speaker || ""} onChange={(e) => setForm({ ...form, speaker: e.target.value })} />
            </div>
            {form.category !== "sponsor" && (
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Data registrazione / diretta <span className="lowercase text-slate-600">(opzionale)</span></label>
                <input
                  data-testid="form-recorded-at"
                  type="datetime-local"
                  className={`${inputCls} [color-scheme:dark]`}
                  value={form.recorded_at || ""}
                  onChange={(e) => setForm({ ...form, recorded_at: e.target.value })}
                />
              </div>
            )}
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("topics")} <span className="lowercase text-slate-600">(eTEP, TAR, Diastasis…)</span></label>
              <input
                data-testid="form-tags"
                className={inputCls}
                placeholder="eTEP, TAR, Diastasis"
                value={form.tags || ""}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("description")}</label>
              <textarea data-testid="form-description" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("vimeo_url")}</label>
              <input data-testid="form-vimeo" className={inputCls} placeholder="https://vimeo.com/123456789" value={form.vimeo_url || ""} onChange={(e) => setForm({ ...form, vimeo_url: e.target.value })} />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">YouTube URL</label>
              <input data-testid="form-youtube" className={inputCls} placeholder="https://www.youtube.com/watch?v=..." value={form.youtube_url || ""} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} />
            </div>
            {form.category === "sponsor" && (
              <>
                <div>
                  <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("sponsor_website")}</label>
                  <input data-testid="form-link-url" className={inputCls} placeholder="https://sponsor.com" value={form.link_url || ""} onChange={(e) => setForm({ ...form, link_url: e.target.value })} />
                </div>
                <div>
                  <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("sponsor_logo")}</label>
                  <div className="flex items-center gap-4">
                    {form.logo_url && (
                      <div className="flex h-14 w-24 shrink-0 items-center justify-center border border-white/10 bg-white/5 p-2">
                        <img src={form.logo_url} alt="logo" className="max-h-full max-w-full object-contain" />
                      </div>
                    )}
                    <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 border border-dashed border-white/20 bg-ink-900/40 px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-slate-300 transition-colors hover:border-cyan hover:text-cyan">
                      <UploadSimple size={15} /> {uploading ? "…" : t("upload_logo")}
                      <input data-testid="form-logo" type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploading} />
                    </label>
                  </div>
                </div>
              </>
            )}
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{t("upload_file")}</label>
              <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-white/20 bg-ink-900/40 px-4 py-4 font-mono text-xs uppercase tracking-widest text-slate-300 transition-colors hover:border-cyan hover:text-cyan">
                <UploadSimple size={16} />
                {uploading ? "Uploading…" : form.file_name || "Select file (PPTX / MP4)"}
                <input data-testid="form-file" type="file" accept=".ppt,.pptx,.mp4,video/mp4" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            </div>
          </div>
          <DialogFooter className="mt-2 gap-3 sm:gap-3">
            <button onClick={() => setOpen(false)} className="border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest text-slate-300 transition-colors hover:text-white">
              {t("cancel")}
            </button>
            <button data-testid="save-content-btn" onClick={save} disabled={saving} className="bg-cyan px-8 py-3 font-mono text-xs font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-white disabled:opacity-50">
              {saving ? "…" : t("save")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New-content digest preview dialog */}
      <Dialog open={digestDialog} onOpenChange={setDigestDialog}>
        <DialogContent data-testid="digest-dialog" className="max-w-lg border-white/10 bg-ink-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-head text-xl font-black uppercase tracking-tight text-white">{t("digest_preview_title")}</DialogTitle>
            <DialogDescription className="font-body text-sm text-slate-400">{t("digest_members_note")}</DialogDescription>
          </DialogHeader>
          {!digestPreview ? (
            <p className="py-8 text-center font-mono text-xs uppercase tracking-widest text-slate-500">…</p>
          ) : digestPreview.new_count === 0 ? (
            <p data-testid="digest-empty" className="py-6 text-center font-body text-sm text-slate-400">{t("digest_none")}</p>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="font-body text-sm text-slate-300">{t("digest_preview_sub").replace("{c}", digestPreview.new_count)}</p>
              <p data-testid="digest-last-sent" className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {digestPreview.last_sent_at
                  ? `${t("digest_last_sent")} ${new Date(digestPreview.last_sent_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
                  : t("digest_never_sent")}
              </p>
              <ul data-testid="digest-content-list" className="max-h-64 overflow-y-auto border border-white/10">
                {digestPreview.contents.map((c, i) => (
                  <li key={i} data-testid={`digest-item-${i}`} className="border-b border-white/5 px-4 py-3 last:border-0">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan">{c.category}</p>
                    <p className="font-body text-sm text-white">{c.title}</p>
                    {c.speaker && <p className="font-body text-xs text-slate-500">{c.speaker}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter className="mt-2 gap-3 sm:gap-3">
            <button onClick={() => setDigestDialog(false)} className="border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-widest text-slate-300 transition-colors hover:text-white">
              {t("cancel")}
            </button>
            <button
              data-testid="digest-confirm-btn"
              onClick={confirmDigest}
              disabled={sendingDigest || !digestPreview || digestPreview.new_count === 0}
              className="bg-cyan px-8 py-3 font-mono text-xs font-bold uppercase tracking-widest text-ink-900 transition-colors hover:bg-white disabled:opacity-50"
            >
              {sendingDigest ? "…" : t("digest_send_now")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create YouTube broadcast dialog */}
      <Dialog open={ytDialog} onOpenChange={setYtDialog}>
        <DialogContent className="max-w-lg border-white/10 bg-ink-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-head text-lg uppercase text-white">Crea diretta YouTube</DialogTitle>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Il broadcast viene creato subito sul tuo canale. L'URL e i campi vengono compilati automaticamente qui.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Titolo *</label>
              <input data-testid="yt-form-title" value={ytForm.title} onChange={(e) => setYtForm({ ...ytForm, title: e.target.value })} className="w-full border border-white/15 bg-ink-900 px-3 py-2 font-body text-sm text-white outline-none focus:border-cyan" />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Descrizione (opzionale)</label>
              <textarea data-testid="yt-form-desc" value={ytForm.description} onChange={(e) => setYtForm({ ...ytForm, description: e.target.value })} rows={2} className="w-full border border-white/15 bg-ink-900 px-3 py-2 font-body text-sm text-white outline-none focus:border-cyan" />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Programmata per <span className="lowercase text-slate-600">(lascia vuoto per diretta improvvisa)</span></label>
              <input data-testid="yt-form-sched" type="datetime-local" value={ytForm.scheduled_at} onChange={(e) => setYtForm({ ...ytForm, scheduled_at: e.target.value })} className="w-full border border-white/15 bg-ink-900 px-3 py-2 font-body text-sm text-white outline-none focus:border-cyan [color-scheme:dark]" />
            </div>
            <div>
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Privacy YouTube</label>
              <select data-testid="yt-form-privacy" value={ytForm.privacy} onChange={(e) => setYtForm({ ...ytForm, privacy: e.target.value })} className="w-full border border-white/15 bg-ink-900 px-3 py-2 font-body text-sm text-white outline-none focus:border-cyan">
                <option value="unlisted">Non in elenco (consigliato)</option>
                <option value="private">Privata</option>
                <option value="public">Pubblica</option>
              </select>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <button onClick={() => setYtDialog(false)} className="border border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-widest text-slate-300 transition-colors hover:text-white">{t("cancel")}</button>
            <button data-testid="yt-submit-btn" onClick={createYouTubeBroadcast} disabled={ytCreating} className="bg-red-600 px-6 py-2 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-red-500 disabled:opacity-50">
              {ytCreating ? "…" : "Crea"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RTMP credentials dialog (for GoPro / OBS / Wirecast configuration) */}
      <Dialog open={rtmpDialog} onOpenChange={(o) => { setRtmpDialog(o); if (!o) setStreamKeyVisible(false); }}>
        <DialogContent className="max-w-2xl border-white/10 bg-ink-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-head text-lg uppercase text-white flex items-center gap-2">
              <Broadcast size={20} className="text-cyan" /> Credenziali RTMP
            </DialogTitle>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 truncate">
              {rtmpInfo?.title || "Diretta YouTube"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <p className="font-body text-sm text-slate-300">
              Usa questi dati per configurare il tuo encoder <b className="text-cyan">GoPro</b>, <b className="text-cyan">OBS Studio</b>, <b className="text-cyan">Wirecast</b> o simile.
              Lo stream apparirà automaticamente nel player integrato del sito.
            </p>

            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Server RTMP</label>
              <div className="flex gap-2">
                <input data-testid="rtmp-url-field" readOnly value={rtmpInfo?.rtmp_url || ""} className="flex-1 border border-white/10 bg-ink-900 px-3 py-2 font-mono text-xs text-cyan outline-none" />
                <button onClick={() => copyToClipboard(rtmpInfo?.rtmp_url || "", "URL RTMP")} className="border border-cyan/50 px-4 font-mono text-[10px] uppercase tracking-widest text-cyan hover:bg-cyan hover:text-ink-900">Copia</button>
              </div>
            </div>

            {rtmpInfo?.rtmp_backup_url ? (
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Server backup (facoltativo)</label>
                <div className="flex gap-2">
                  <input readOnly value={rtmpInfo.rtmp_backup_url} className="flex-1 border border-white/10 bg-ink-900 px-3 py-2 font-mono text-xs text-slate-300 outline-none" />
                  <button onClick={() => copyToClipboard(rtmpInfo.rtmp_backup_url, "URL backup")} className="border border-white/20 px-4 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-cyan hover:text-cyan">Copia</button>
                </div>
              </div>
            ) : null}

            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Stream Key <span className="text-red-400">(non condividere)</span></label>
              <div className="flex gap-2">
                <input data-testid="rtmp-key-field" readOnly value={streamKeyVisible ? (rtmpInfo?.stream_key || "") : "•".repeat((rtmpInfo?.stream_key || "").length || 24)} className="flex-1 border border-red-500/40 bg-ink-900 px-3 py-2 font-mono text-xs text-red-300 outline-none" />
                <button onClick={() => setStreamKeyVisible((v) => !v)} className="border border-white/20 px-3 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-cyan hover:text-cyan">{streamKeyVisible ? "Nascondi" : "Mostra"}</button>
                <button onClick={() => copyToClipboard(rtmpInfo?.stream_key || "", "Stream Key")} className="border border-cyan/50 px-4 font-mono text-[10px] uppercase tracking-widest text-cyan hover:bg-cyan hover:text-ink-900">Copia</button>
              </div>
              <p className="mt-1 font-body text-[11px] text-slate-500">
                La Stream Key è privata e valida solo per questa diretta. Se la condividi, chiunque può trasmettere sul tuo canale.
              </p>
            </div>

            {rtmpInfo?.watch_url ? (
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Watch URL (pubblico)</label>
                <div className="flex gap-2">
                  <input readOnly value={rtmpInfo.watch_url} className="flex-1 border border-white/10 bg-ink-900 px-3 py-2 font-mono text-xs text-slate-300 outline-none" />
                  <button onClick={() => copyToClipboard(rtmpInfo.watch_url, "Watch URL")} className="border border-white/20 px-4 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-cyan hover:text-cyan">Copia</button>
                </div>
              </div>
            ) : null}

            <div className="border-l-2 border-cyan/60 bg-cyan/5 p-3 font-body text-xs text-slate-300">
              <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-cyan">📖 Come streamare da GoPro</p>
              Vai su <Link to="/admin/help" className="underline decoration-cyan/60 hover:text-cyan">Admin → Guide</Link> → <b>Guida GoPro Live</b> per la procedura passo-passo (GoPro HERO 8+, GoPro Quik app, OBS Studio).
            </div>
          </div>

          <DialogFooter className="mt-2">
            <button onClick={() => { setRtmpDialog(false); setStreamKeyVisible(false); }} className="bg-cyan px-6 py-2 font-mono text-xs font-bold uppercase tracking-widest text-ink-900 hover:bg-white">Ho copiato tutto</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Viewers detail modal */}
      <Dialog open={!!viewersFor} onOpenChange={(o) => !o && setViewersFor(null)}>
        <DialogContent className="max-w-2xl border-white/10 bg-ink-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-head text-lg uppercase text-white">Chi ha guardato</DialogTitle>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 truncate">
              {viewersFor?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {(viewersFor?.viewers || []).length === 0 ? (
              <p className="py-8 text-center font-body text-sm text-slate-500">Ancora nessuna visualizzazione da membri autenticati.</p>
            ) : (
              <table className="w-full text-left font-body text-sm">
                <thead>
                  <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <th className="py-2">Membro</th>
                    <th className="py-2 text-right">Aperture</th>
                    <th className="py-2 text-right">Tempo visto</th>
                    <th className="py-2 text-right">Ultima visita</th>
                  </tr>
                </thead>
                <tbody>
                  {viewersFor.viewers.map((v) => (
                    <tr key={v.user_id} data-testid={`viewer-${v.user_id}`} className="border-b border-white/5">
                      <td className="py-2">
                        <p className="text-slate-200">{v.user_name || "—"}</p>
                        <p className="font-mono text-[10px] text-slate-500">{v.user_email}</p>
                      </td>
                      <td className="py-2 text-right font-mono text-xs text-cyan">{v.opens}</td>
                      <td className="py-2 text-right font-mono text-xs text-slate-300">{fmtWatch(v.total_seconds)}</td>
                      <td className="py-2 text-right font-mono text-[10px] text-slate-500">
                        {v.last_seen ? new Date(v.last_seen).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {transcriptFor && (
        <TranscriptManagerModal content={transcriptFor} onClose={() => setTranscriptFor(null)} />
      )}
    </div>
  );
}

function TimelineChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.views || 0));
  const w = 600;
  const h = 120;
  const barW = w / data.length;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none">
      <line x1="0" y1={h - 20} x2={w} y2={h - 20} stroke="#1e293b" strokeWidth="1" />
      {data.map((d, i) => {
        const bh = ((d.views || 0) / max) * (h - 24);
        return (
          <g key={d.date}>
            <rect
              x={i * barW + 1}
              y={h - 20 - bh}
              width={Math.max(1, barW - 2)}
              height={bh}
              fill={d.views > 0 ? "#00F0FF" : "#1e293b"}
              opacity={d.views > 0 ? 0.9 : 0.4}
            >
              <title>{`${d.date}: ${d.views} views`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
}
